import {Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Drawer} from '@mui/material';
import {ReactComponent as JudgeIcon} from '@icons/sidebar/judge.svg';
import {styles} from './WinnerDetailsStyle.styles';
import {BODY1, IMAGE_TABLE_ALT, ITEM, SUBTITLE1, TEXT, WIN_BID} from '@/utils/constants';
import {MENU_OPTION} from '@/utils/enums';
import Button from '@/shared/components/Button';
import {useContext, useEffect} from 'react';
import {ReactComponent as CloseIcon} from '@icons/close_big.svg';
import {MobHeaderContext} from '@/layouts/OrganizationTemplate/OrganizationHeader/OrganizationMobHeader';
import {useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getAuction, getAuctionLoaderSelector, getAuctionSelector} from '@/store/organization-auction';
import Loader from '@/shared/components/Loader';
import {OrganizationAuctionService} from '@/utils/services';
import {orgAPI} from '@/core/api';

export const tableActionEntryMenuContent = [
	MENU_OPTION.GO_TO_PROFILE,
	MENU_OPTION.MARK_AS_PAID,
	MENU_OPTION.REMOVE,
	MENU_OPTION.CANCEL,
];

const WinnersDetailsPage = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const {id} = useParams<{id: string}>();

	const auctionItem = useAppSelector(getAuctionSelector);
	const isLoading = useAppSelector(getAuctionLoaderSelector);

	const handleDeleteAuction = async (auctionId: number) => {
		try {
			await OrganizationAuctionService.deleteAuction(auctionId);
			setIsOpen(false);
			toast.success('Auction item deleted successfully');
			navigate(-1);
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

	const handleMakeAuctionPaid = async (auctionId: number) => {
		try {
			await OrganizationAuctionService.updateAuctionPaymentStatus(auctionId, true);
			setIsOpen(false);
			toast.success('Auction item marked as paid successfully');
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

	const handleAction = async (action: MENU_OPTION) => {
		switch (action) {
			case MENU_OPTION.CANCEL:
				setIsOpen(false);
				break;

			case MENU_OPTION.REMOVE:
				if (id) {
					await handleDeleteAuction(+id);
				}
				break;

			case MENU_OPTION.MARK_AS_PAID:
				if (id) {
					await handleMakeAuctionPaid(+id);
				}
				break;

			case MENU_OPTION.GO_TO_PROFILE:
				if (id) {
					navigate(`/organization/attendees/197`);
				}
				break;

			default:
				break;
		}
	};

	const getWonItem = () => {
		if (!id) return;

		try {
			dispatch(getAuction({id: +id, apiInstance: orgAPI})).unwrap();
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

	useEffect(() => {
		getWonItem();
	}, [id]);

	const {isOpen, setIsOpen} = useContext(MobHeaderContext);
	return (
		<Box>
			{isLoading ? (
				<Loader />
			) : (
				<Box>
					<Box sx={styles.attendeeDetails}>
						<Box sx={styles.block}>
							<Typography sx={styles.text}>Bid #</Typography>
							<Typography sx={styles.description}>{auctionItem?.winnerDetails?.bidNumber}</Typography>
						</Box>
						<Box sx={styles.block}>
							<Typography sx={styles.text}>Table #</Typography>
							<Typography sx={styles.description}>{auctionItem?.winnerDetails?.tableNumber}</Typography>
						</Box>
						<Box sx={styles.block}>
							<Typography sx={styles.text}>Table name</Typography>
							<Typography sx={styles.description}>{auctionItem?.winnerDetails?.tableName}</Typography>
						</Box>
						<Box sx={styles.block}>
							<Typography sx={styles.text}>Type of seat</Typography>
							<Typography sx={styles.description}>{auctionItem?.winnerDetails?.typeOfSeat}</Typography>
						</Box>
						<Box sx={styles.block}>
							<Typography sx={styles.text}>Table purchaser</Typography>
							<Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
								<Typography sx={styles.description}>{auctionItem?.winnerDetails?.tablePurchaserFullName}</Typography>
								<Typography sx={styles.text}>{auctionItem?.winnerDetails?.tablePurchaserEmail}</Typography>
							</Box>
						</Box>
						<Box sx={styles.block}>
							<Typography sx={styles.text}>Contact phone</Typography>
							<Typography sx={styles.description}>{auctionItem?.winnerDetails?.contactPhoneNumber}</Typography>
						</Box>
					</Box>
					<TableContainer sx={styles.container}>
						<Table sx={styles.table}>
							<TableHead>
								<TableRow sx={styles.tableHeader}>
									<TableCell>{ITEM}</TableCell>

									<TableCell>{WIN_BID}</TableCell>
								</TableRow>
							</TableHead>

							<TableBody>
								<TableRow sx={styles.tableBody}>
									<TableCell>
										<Box sx={styles.bodyCell}>
											<Box sx={styles.bodyCellImage}>
												{auctionItem?.itemImages?.[0]?.image ? (
													<img src={auctionItem?.itemImages[0].image} alt={IMAGE_TABLE_ALT} />
												) : (
													<JudgeIcon />
												)}
												{/* <img src={auctionItem?.itemImages?.[0].image} alt={IMAGE_TABLE_ALT} /> */}
											</Box>
											<Box sx={styles.bodyCellImageContent}>
												<Typography variant={SUBTITLE1} sx={styles.bodyCellImageContentName}>
													{auctionItem?.name}
												</Typography>
												<Typography variant={BODY1} sx={styles.bodyCellImageContentCategory}>
													{auctionItem?.categoryId}
												</Typography>
											</Box>
										</Box>
									</TableCell>

									<TableCell>{auctionItem?.winnerDetails?.winBidAmount}</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</TableContainer>
					<Drawer anchor="bottom" open={isOpen} onClose={() => setIsOpen(false)}>
						<Box sx={styles.drawerHeader}>
							<Typography
								sx={styles.drawerTitle}
							>{`${auctionItem?.winnerDetails?.firstName} ${auctionItem?.winnerDetails?.lastName}`}</Typography>
							<CloseIcon onClick={() => setIsOpen(false)} />
						</Box>
						<Box sx={styles.tableActionEntryMenu}>
							{tableActionEntryMenuContent.map((item: MENU_OPTION) => {
								return <Button key={item} variant={TEXT} title={item} onClick={() => handleAction(item)} />;
							})}
						</Box>
					</Drawer>
				</Box>
			)}
		</Box>
	);
};

export default WinnersDetailsPage;
